
import React, { useState } from 'react';
import * as firebaseAuth from 'firebase/auth';
import { auth } from '../services/firebase';
import Logo from './Logo';
import TermsOfServiceModal from './TermsOfServiceModal';
import BetaStamp from './BetaStamp';
import type { User } from '../types';
import { getTranslation } from '../utils/translations';
import { shouldUseRedirectAuth } from '../utils/authBrowser';
import { detectBrowserLanguage } from '../utils/appLanguage';

const { 
    GoogleAuthProvider, 
    signInWithPopup,
    signInWithRedirect,
    signInWithEmailAndPassword, 
    createUserWithEmailAndPassword 
} = firebaseAuth as any;

interface LoginProps {
    onLogin: (email: string) => void;
    users: User[];
    authError?: string | null;
    onClearAuthError?: () => void;
    onBack?: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin, users, authError, onClearAuthError, onBack }) => {
    const [error, setError] = useState('');
    const [detailedError, setDetailedError] = useState<{title: string, message: string, code?: string} | null>(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoginView, setIsLoginView] = useState(true); 
    const [loading, setLoading] = useState(false);
    
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [isTosModalOpen, setIsTosModalOpen] = useState(false);

    const browserLang = detectBrowserLanguage();
    const t = (key: any) => getTranslation(browserLang, key);

    const handleGoogleAuthError = (err: any) => {
        console.error("Google login failed", err);
        const errorCode = err.code || '';
        const errorMessage = err.message || '';

        if (errorCode === 'auth/unauthorized-domain') {
            setDetailedError({
                title: 'Configuration Required',
                message: 'To use Google Sign-In, you must add the current domain to your Firebase Authorized Domains list.',
                code: 'auth/unauthorized-domain',
            });
        } else if (errorCode === 'auth/popup-closed-by-user') {
            // Ignore
        } else if (errorCode.includes('invalid-credential') || errorMessage.includes('invalid-credential')) {
            setError('Login failed. Please check your account details or try standard email login.');
        } else {
            setError(errorMessage || 'Google login failed.');
        }
    };

    const handleGoogleLogin = async () => {
        if (!isLoginView && !acceptedTerms) {
            setError(t('agreeToTerms'));
            return;
        }

        setLoading(true);
        setError('');
        setDetailedError(null);
        onClearAuthError?.();

        if (!isLoginView) {
            sessionStorage.setItem('acceptedTermsOnRegister', 'true');
        }

        const provider = new GoogleAuthProvider();
        try {
            if (shouldUseRedirectAuth()) {
                await signInWithRedirect(auth, provider);
                return;
            }
            await signInWithPopup(auth, provider);
        } catch (err: any) {
            const errorCode = err.code || '';
            if (errorCode === 'auth/popup-blocked' || errorCode === 'auth/cancelled-popup-request') {
                try {
                    await signInWithRedirect(auth, provider);
                    return;
                } catch (redirectErr: any) {
                    handleGoogleAuthError(redirectErr);
                }
            } else {
                handleGoogleAuthError(err);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEmailAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!isLoginView && !acceptedTerms) {
            setError(t('agreeToTerms'));
            return;
        }

        setLoading(true);
        setError('');
        setDetailedError(null);
        
        const cleanEmail = email.trim();

        try {
            if (isLoginView) {
                await signInWithEmailAndPassword(auth, cleanEmail, password);
            } else {
                await createUserWithEmailAndPassword(auth, cleanEmail, password);
                sessionStorage.setItem('acceptedTermsOnRegister', 'true');
            }
        } catch (err: any) {
            console.error("Email auth failed", err);
            const errorCode = err.code || '';
            const errorMessage = err.message || '';

            if (errorCode === 'auth/email-already-in-use') {
                setDetailedError({
                    title: 'Account Exists',
                    message: 'This email is already registered. Please login.',
                    code: 'auth/email-already-in-use'
                });
            } else if (errorCode === 'auth/operation-not-allowed') {
                 setDetailedError({
                    title: 'Config Error',
                    message: 'Email/Password sign-in is not enabled in Firebase Console.',
                    code: 'auth/operation-not-allowed'
                 });
            } else if (errorCode.includes('invalid-credential') || errorCode === 'auth/wrong-password' || errorCode === 'auth/user-not-found' || errorMessage.includes('invalid-credential')) {
                setError("Incorrect email or password. Please try again or create a new account.");
            } else if (errorCode === 'auth/too-many-requests') {
                setError("Too many failed attempts. Please wait a moment or reset your password.");
            } else if (errorCode === 'auth/weak-password') {
                setError("Password must be at least 6 characters.");
            } else if (errorCode === 'auth/invalid-email') {
                setError("Please enter a valid email address.");
            } else {
                setError("Authentication error: " + (errorCode || "Unknown Error"));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-50 to-slate-200 flex flex-col items-center justify-center z-50 p-4 text-slate-800 font-rubik" dir="ltr">
            <div className="w-full max-w-md bg-white p-8 sm:p-10 rounded-3xl shadow-2xl text-center overflow-y-auto max-h-[90vh] border border-white/50 relative">
                {onBack && (
                    <button onClick={onBack} className="absolute top-4 left-4 p-2 text-slate-400 hover:text-slate-600 transition-colors bg-slate-50 hover:bg-slate-100 rounded-full focus:outline-none" aria-label="Go back">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
                    </button>
                )}
                <div className="flex flex-col items-center mb-8 relative">
                    <div className="w-32 h-32 mb-4 bg-slate-50 rounded-full flex items-center justify-center shadow-inner relative">
                        <Logo className="w-24 h-24" />
                    </div>
                    <div className="relative">
                        <h1 className="text-4xl font-black text-slate-800 tracking-tight">mealPai</h1>
                        <BetaStamp className="absolute -top-1 -right-8 transform rotate-12" />
                    </div>
                    <p className="text-slate-500 mt-2 font-medium">{isLoginView ? t('loginHeader') : t('registerHeader')}</p>
                </div>

                {authError && (
                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-6 text-sm text-left animate-fade-in" role="alert">
                        <span className="font-bold mr-1">{t('notice')}:</span>
                        <span>{authError}</span>
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-6 text-sm text-left animate-fade-in" role="alert">
                        <span className="font-bold mr-1">{t('notice')}:</span>
                        <span>{error}</span>
                    </div>
                )}

                {detailedError && (
                    <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl mb-6 text-sm text-left animate-fade-in">
                        <p className="font-black uppercase text-[10px] tracking-widest mb-1">{detailedError.title}</p>
                        <p>{detailedError.message}</p>
                    </div>
                )}

                <button 
                    onClick={handleGoogleLogin}
                    disabled={loading || (!isLoginView && !acceptedTerms)}
                    className="w-full py-3.5 px-4 bg-white border-2 border-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-50 hover:border-slate-300 transition-all focus:outline-none focus:ring-4 focus:ring-slate-100 flex items-center justify-center gap-3 mb-6 disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                    <svg className="w-5 h-5 transition-transform group-hover:scale-110" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                    {loading ? '...' : t('googleLogin')}
                </button>

                <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-slate-200"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-3 bg-white text-slate-400 font-medium">{t('orWithEmail')}</span>
                    </div>
                </div>

                <form onSubmit={handleEmailAuth} className="space-y-4 text-left">
                    <input
                        type="email"
                        placeholder={t('emailPlaceholder')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 text-left bg-slate-50 transition-all outline-none"
                    />
                    <input
                        type="password"
                        placeholder={t('passwordPlaceholder')}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 text-left bg-slate-50 transition-all outline-none"
                    />

                    {!isLoginView && (
                        <div className="flex items-start gap-2 pt-1 text-sm text-slate-600">
                            <input
                                type="checkbox"
                                id="tos-checkbox"
                                checked={acceptedTerms}
                                onChange={(e) => setAcceptedTerms(e.target.checked)}
                                className="mt-1 w-4 h-4 text-sky-600 rounded border-gray-300 focus:ring-sky-500 cursor-pointer"
                            />
                            <label htmlFor="tos-checkbox" className="cursor-pointer">
                                {t('agreeToTerms')} <button type="button" onClick={() => setIsTosModalOpen(true)} className="text-sky-600 hover:text-sky-800 hover:underline font-medium focus:outline-none">{t('termsOfService')}</button>
                            </label>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading || (!isLoginView && !acceptedTerms)}
                        className="w-full py-3.5 px-4 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-all shadow-lg disabled:opacity-50 mt-2"
                    >
                        {loading ? '...' : (isLoginView ? t('loginBtn') : t('registerBtn'))}
                    </button>
                </form>

                <div className="mt-8 text-sm">
                    <button 
                        onClick={() => {
                            setIsLoginView(!isLoginView);
                            setError('');
                            setDetailedError(null);
                        }} 
                        className="text-sky-600 hover:text-sky-800 font-semibold hover:underline"
                    >
                        {isLoginView ? t('noAccount') : t('existingAccount')}
                    </button>
                </div>
            </div>
            
             <div className="mt-8 text-center text-slate-400 text-xs">
                <p>&copy; {new Date().getFullYear()} mealPai. All rights reserved.</p>
            </div>

            <TermsOfServiceModal 
                isOpen={isTosModalOpen} 
                onClose={() => setIsTosModalOpen(false)} 
                language={browserLang}
            />
        </div>
    );
};

export default Login;
